/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Phone, Video, MoreVertical, Send, Paperclip, Smile,
  Reply, Trash2, Edit2, Copy, X, Check, CheckCheck,
  Search, Star, Pin, Forward, Download, Mic,
  PhoneOff, MicOff, VideoOff, Volume2,
  Clock, Lock, Image as ImageIcon, File, MapPin,
  MoreHorizontal
} from 'lucide-react';
import { messagesAPI } from '../../utils/api';
import { getSocket } from '../../utils/socket';
import { useSocket } from '../../context/SocketContext';
import { useToast } from '../../context/ToastContext';
import { getAvatarUrl, formatTime, formatMessageDate } from '../../utils/helpers';
import './Chat.css';

const EMOJIS = ['👍','❤️','😂','😮','😢','🔥','🎉','👏','🙏','💯','😍','🤣','😊','✅','🚀'];
const ATTACH_TYPES = [
  { icon:'📷', label:'Camera',   info:'Take photo or video' },
  { icon:'🖼️', label:'Gallery',  info:'Photo & video' },
  { icon:'📄', label:'Document', info:'PDF, DOC, XLS...' },
  { icon:'🎵', label:'Audio',    info:'MP3, WAV...' },
  { icon:'📍', label:'Location', info:'Share location' },
  { icon:'👤', label:'Contact',  info:'Share contact' },
  { icon:'📊', label:'Poll',     info:'Create poll' },
  { icon:'🎁', label:'GIF',      info:'Send GIF' },
];

// ── Message component ──────────────────────────────────────────────
function Message({ msg, isMe, otherUser, currentUser, onReact, onDelete, onReply, onEdit, onStar, onForward, onPin, jumpToMsg }) {
  const [showMenu, setShowMenu]   = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [editing, setEditing]     = useState(false);
  const [editText, setEditText]   = useState(msg.content);
  const toast = useToast();

  const sId = typeof msg.sender === 'object' ? (msg.sender._id || msg.sender.id) : msg.sender;
  const senderAvatar = isMe ? getAvatarUrl(currentUser) : getAvatarUrl(msg.senderInfo || otherUser);
  const isStarred = (msg.starred || []).includes(currentUser._id || currentUser.id);
  const isRead = (msg.readBy || []).some(r => {
    const rid = typeof r === 'object' ? (r._id || r.id) : r;
    return rid !== (currentUser._id || currentUser.id);
  });

  const close = () => { setShowMenu(false); setShowEmoji(false); };

  const copy = () => { navigator.clipboard.writeText(msg.content); toast.success('Copied!'); close(); };
  const star = () => { onStar(msg._id); close(); };
  const pin  = () => { onPin(msg); close(); };
  const fwd  = () => { onForward(msg); close(); };
  const del  = (forAll) => { onDelete(msg._id, forAll); close(); };

  // Reply preview
  const replyPreview = msg.replyPreview || (msg.replyTo && typeof msg.replyTo === 'object' ? msg.replyTo : null);

  return (
    <div className={`msg-row ${isMe ? 'mine' : 'theirs'}`} id={`msg-${msg._id}`}>
      {/* Avatar for other person */}
      {!isMe && <img src={senderAvatar} alt="" className="avatar msg-avatar" style={{ width:30, height:30 }}/>}

      <div className="msg-wrap">
        {/* Reply preview */}
        {replyPreview && (
          <div className={`reply-preview ${isMe ? 'me' : ''}`}
            onClick={() => replyPreview._id && jumpToMsg(replyPreview._id)}>
            <div className="reply-bar"/>
            <div style={{ minWidth:0 }}>
              <span className="reply-from">{isMe ? 'You' : otherUser.displayName}</span>
              <p>{replyPreview.content?.slice(0,60) || `📎 ${replyPreview.type}`}</p>
            </div>
          </div>
        )}

        {/* Bubble */}
        <div className={`msg-bubble ${isMe?'bubble-me':'bubble-them'} ${msg.deleted?'deleted':''} ${isStarred?'starred':''}`}
          onContextMenu={e => { e.preventDefault(); setShowMenu(true); }}>

          {msg.deleted ? (
            <em className="deleted-text">🚫 This message was deleted</em>
          ) : editing ? (
            <div className="edit-wrap">
              <input autoFocus value={editText}
                onChange={e => setEditText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') { onEdit(msg._id, editText); setEditing(false); }
                  if (e.key === 'Escape') setEditing(false);
                }} className="edit-input"/>
              <button onClick={() => { onEdit(msg._id, editText); setEditing(false); }}><Check size={13}/></button>
              <button onClick={() => setEditing(false)}><X size={13}/></button>
            </div>
          ) : (
            <>
              {/* Voice message */}
              {msg.type === 'voice' && (
                <div className="voice-bubble">
                  <button className="voice-play"><Volume2 size={16}/></button>
                  <div className="voice-wave">
                    {Array(24).fill(0).map((_, i) => <div key={i} className="wave-bar" style={{ height: `${4+Math.random()*14}px` }}/>)}
                  </div>
                  <span className="voice-dur">{msg.duration || '0:00'}</span>
                </div>
              )}
              {/* Image */}
              {msg.type === 'image' && msg.fileUrl && (
                <img src={msg.fileUrl} alt={msg.caption||'image'} className="msg-image"
                  onClick={() => window.open(msg.fileUrl,'_blank')}/>
              )}
              {/* Video */}
              {msg.type === 'video' && msg.fileUrl && (
                <video src={msg.fileUrl} controls className="msg-video" style={{ maxWidth:260, borderRadius:8 }}/>
              )}
              {/* File */}
              {msg.type === 'file' && (
                <div className="file-bubble">
                  <File size={28} style={{ color:'var(--primary-light)', flexShrink:0 }}/>
                  <div>
                    <p>{msg.fileName || 'File'}</p>
                    <span>{msg.fileSize ? `${(msg.fileSize/1024).toFixed(1)} KB` : ''}</span>
                  </div>
                  <a href={msg.fileUrl} download={msg.fileName} target="_blank" rel="noreferrer">
                    <Download size={16}/>
                  </a>
                </div>
              )}
              {/* Location */}
              {msg.type === 'location' && msg.location && (
                <div className="location-bubble"
                  onClick={() => window.open(`https://maps.google.com/?q=${msg.location.lat},${msg.location.lng}`,'_blank')}>
                  <MapPin size={20} style={{ color:'var(--danger)' }}/>
                  <div>
                    <p>📍 Location</p>
                    <span>{msg.location.address || `${msg.location.lat}, ${msg.location.lng}`}</span>
                  </div>
                </div>
              )}
              {/* Text content */}
              {msg.content && <p className="msg-text">{msg.content}</p>}
              {/* Caption */}
              {msg.caption && msg.type !== 'text' && <p className="msg-caption">{msg.caption}</p>}
              {/* Edited */}
              {msg.edited && <span className="edited-tag">edited</span>}
              {/* Star */}
              {isStarred && <span className="star-mark">⭐</span>}
              {/* Disappearing timer */}
              {msg.disappearsAt && <span className="disappear-tag"><Clock size={10}/></span>}
              {/* Encryption badge */}
              <span className="lock-badge" title="End-to-end encrypted"><Lock size={9}/></span>
            </>
          )}
        </div>

        {/* Reactions */}
        {msg.reactions?.length > 0 && (
          <div className="reactions-row">
            {msg.reactions.map(r => (
              <button key={r.emoji} className="react-pill"
                onClick={() => onReact(msg._id, r.emoji, isMe ? 'mine' : 'theirs')}
                title={`${r.users?.length || 0} people`}>
                {r.emoji} {r.users?.length > 1 ? r.users.length : ''}
              </button>
            ))}
          </div>
        )}

        {/* Meta: time + status */}
        <div className={`msg-meta ${isMe?'me':''}`}>
          <span className="msg-time">{formatTime(msg.createdAt)}</span>
          {isMe && !msg.deleted && (
            isRead
              ? <CheckCheck size={13} style={{ color:'#00B4D8' }} title="Seen"/>
              : <Check size={13} style={{ color:'var(--text-muted)' }} title="Sent"/>
          )}
        </div>

        {/* Hover action bar */}
        <div className={`hover-actions ${isMe?'left':'right'}`}>
          <button title="React" onClick={() => { setShowEmoji(v=>!v); setShowMenu(false); }}>
            <Smile size={13}/>
          </button>
          <button title="Reply" onClick={() => { onReply(msg); close(); }}>
            <Reply size={13}/>
          </button>
          {isMe && !msg.deleted && (
            <button title="Edit" onClick={() => { setEditing(true); close(); }}>
              <Edit2 size={13}/>
            </button>
          )}
          <button title="More" onClick={() => { setShowMenu(v=>!v); setShowEmoji(false); }}>
            <MoreHorizontal size={13}/>
          </button>
        </div>

        {/* Emoji reaction picker */}
        {showEmoji && (
          <div className={`emoji-quick ${isMe?'left':'right'}`}>
            {EMOJIS.slice(0,8).map(e => (
              <button key={e} onClick={() => { onReact(msg._id, e, isMe?'mine':'theirs'); setShowEmoji(false); }}>
                {e}
              </button>
            ))}
          </div>
        )}

        {/* Context menu */}
        {showMenu && (
          <div className={`ctx-menu ${isMe?'left':'right'}`}>
            <button onClick={() => { onReply(msg); close(); }}><Reply size={13}/> Reply</button>
            <button onClick={copy}><Copy size={13}/> Copy</button>
            <button onClick={star}><Star size={13}/> {isStarred?'Unstar':'Star'}</button>
            <button onClick={fwd}><Forward size={13}/> Forward</button>
            {isMe && <button onClick={pin}><Pin size={13}/> Pin</button>}
            {isMe && !msg.deleted && (
              <>
                <button onClick={() => { setEditing(true); close(); }}><Edit2 size={13}/> Edit</button>
                <div className="ctx-sep"/>
                <button className="ctx-danger" onClick={() => del(false)}><Trash2 size={13}/> Delete for me</button>
                <button className="ctx-danger" onClick={() => del(true)}><Trash2 size={13}/> Delete for everyone</button>
              </>
            )}
          </div>
        )}
      </div>

      {(showMenu||showEmoji) && <div className="overlay-close" onClick={close}/>}
    </div>
  );
}

// ── Main DMChat ────────────────────────────────────────────────────
export default function DMChat({ otherUser, currentUser }) {
  const [messages, setMessages]     = useState([]);
  const [input, setInput]           = useState('');
  const [loading, setLoading]       = useState(true);
  const [replyTo, setReplyTo]       = useState(null);
  const [isTyping, setIsTyping]     = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [showEmoji, setShowEmoji]   = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showMore, setShowMore]     = useState(false);
  const [searchQ, setSearchQ]       = useState('');
  const [isRecording, setRecording] = useState(false);
  const [recTime, setRecTime]       = useState(0);
  const [pinnedMsg, setPinnedMsg]   = useState(null);
  const [callState, setCallState]   = useState(null);
  const [callType, setCallType]     = useState(null);
  const [callDur, setCallDur]       = useState(0);
  const [muted, setMuted]           = useState(false);
  const [camOff, setCamOff]         = useState(false);
  const [page, setPage]             = useState(1);
  const [hasMore, setHasMore]       = useState(true);

  const { isOnline } = useSocket();
  const toast        = useToast();
  const endRef       = useRef(null);
  const inputRef     = useRef(null);
  const typingRef    = useRef(null);
  const callTimerRef = useRef(null);
  const recTimerRef  = useRef(null);

  const otherId = otherUser._id || otherUser.id;
  const myId    = currentUser._id || currentUser.id;
  const online  = isOnline(otherId);

  // ── Load messages ──────────────────────────────────────────────
  const loadMessages = useCallback(async (pg = 1) => {
    try {
      const { data } = await messagesAPI.getDMs(otherId, pg);
      if (pg === 1) setMessages(data);
      else setMessages(prev => [...data, ...prev]);
      if (data.length < 50) setHasMore(false);
    } catch { toast.error('Could not load messages'); }
    finally { setLoading(false); }
  }, [otherId, toast]);

  useEffect(() => {
    setLoading(true); setPage(1); setHasMore(true); setMessages([]);
    loadMessages(1);
    // Mark read
    messagesAPI.markRead(otherId).catch(() => {});
  }, [otherId]);

  // ── Socket events ──────────────────────────────────────────────
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    socket.emit('dm:join', { userId: myId, otherUserId: otherId });

    const onMsg = (msg) => {
      setMessages(prev => prev.find(m => m._id === msg._id) ? prev : [...prev, msg]);
      // Mark read immediately if chat is open
      messagesAPI.markRead(otherId).catch(() => {});
      socket.emit('dm:read', { readerId: myId, senderId: otherId });
    };

    // FIXED: single typing indicator
    const onTyping = ({ userId, isTyping: t }) => {
      if (userId === otherId) setIsTyping(t);
    };

    const onRead = ({ readerId }) => {
      if (readerId === otherId) {
        setMessages(prev => prev.map(m => {
          const sid = typeof m.sender === 'object' ? (m.sender._id||m.sender.id) : m.sender;
          if (sid === myId && !(m.readBy||[]).includes(otherId)) {
            return { ...m, readBy: [...(m.readBy||[]), otherId] };
          }
          return m;
        }));
      }
    };

    const onCallAnswer  = () => { setCallState('active'); startCallTimer(); };
    const onCallRejected= ({ reason }) => { endCall(); toast.info(`Call ${reason || 'rejected'}`); };
    const onCallEnded   = () => { endCall(); };
    const onCallBusy    = () => { endCall(); toast.info(`${otherUser.displayName} is busy`); };

    socket.on('dm:message',    onMsg);
    socket.on('dm:typing',     onTyping);
    socket.on('dm:read:ack',   onRead);
    socket.on('call:answered', onCallAnswer);
    socket.on('call:rejected', onCallRejected);
    socket.on('call:ended',    onCallEnded);
    socket.on('call:busy',     onCallBusy);

    return () => {
      socket.off('dm:message',    onMsg);
      socket.off('dm:typing',     onTyping);
      socket.off('dm:read:ack',   onRead);
      socket.off('call:answered', onCallAnswer);
      socket.off('call:rejected', onCallRejected);
      socket.off('call:ended',    onCallEnded);
      socket.off('call:busy',     onCallBusy);
    };
  }, [myId, otherId, otherUser]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);

  // ── Call helpers ───────────────────────────────────────────────
  const startCallTimer = () => {
    setCallDur(0);
    callTimerRef.current = setInterval(() => setCallDur(d => d+1), 1000);
  };
  const endCall = useCallback(() => {
    clearInterval(callTimerRef.current);
    const socket = getSocket();
    if (socket && callState) socket.emit('call:end', { to: otherId });
    setCallState(null); setCallType(null); setCallDur(0); setMuted(false); setCamOff(false);
  }, [callState, otherId]);

  const startCall = useCallback((type) => {
    if (!online) { toast.error(`${otherUser.displayName} is offline`); return; }
    const socket = getSocket();
    if (!socket?.connected) { toast.error('Not connected to server'); return; }
    setCallType(type); setCallState('calling');
    socket.emit('call:offer', {
      to: otherId, offer: { type:'offer', sdp:'' }, callType: type,
      callerId: myId,
      callerInfo: { displayName: currentUser.displayName, avatar: currentUser.avatar }
    });
  }, [online, otherId, myId, currentUser, otherUser, toast]);

  // ── Typing indicator (FIXED: emits stop when input clears) ─────
  const handleTyping = useCallback(() => {
    const socket = getSocket();
    if (!socket) return;
    socket.emit('dm:typing', { senderId: myId, recipientId: otherId, isTyping: true });
    clearTimeout(typingRef.current);
    typingRef.current = setTimeout(() => {
      socket.emit('dm:typing', { senderId: myId, recipientId: otherId, isTyping: false });
    }, 2000);
  }, [myId, otherId]);

  // Stop typing when input becomes empty
  useEffect(() => {
    if (!input) {
      const socket = getSocket();
      if (socket) socket.emit('dm:typing', { senderId: myId, recipientId: otherId, isTyping: false });
      clearTimeout(typingRef.current);
    }
  }, [input, myId, otherId]);

  // ── Send message ───────────────────────────────────────────────
  const handleSend = useCallback(async () => {
    const content = input.trim();
    if (!content) return;
    setInput(''); setReplyTo(null); setShowEmoji(false);

    // Stop typing
    const socket = getSocket();
    if (socket) socket.emit('dm:typing', { senderId: myId, recipientId: otherId, isTyping: false });
    clearTimeout(typingRef.current);

    const tempId = `tmp_${Date.now()}`;
    const tempMsg = {
      _id: tempId, sender: myId, recipient: otherId, content,
      type: 'text', replyTo: replyTo?._id,
      replyPreview: replyTo ? { content: replyTo.content, type: replyTo.type } : null,
      createdAt: new Date().toISOString(), readBy: [myId],
    };
    setMessages(prev => [...prev, tempMsg]);

    if (socket?.connected) {
      socket.emit('dm:send', {
        senderId: myId, recipientId: otherId, content,
        type: 'text', replyTo: replyTo?._id,
      });
    } else {
      try {
        const { data } = await messagesAPI.sendDM(otherId, { content, replyTo: replyTo?._id });
        setMessages(prev => prev.map(m => m._id === tempId ? data : m));
      } catch { toast.error('Failed to send'); setMessages(prev => prev.filter(m => m._id !== tempId)); }
    }
  }, [input, myId, otherId, replyTo, toast]);

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // ── Actions ────────────────────────────────────────────────────
  const handleReact = async (msgId, emoji) => {
    try {
      const reactions = await messagesAPI.reactToMessage(msgId, emoji);
      setMessages(prev => prev.map(m => m._id === msgId ? { ...m, reactions: reactions.data } : m));
    } catch { toast.error('Reaction failed'); }
  };

  const handleDelete = async (msgId, forEveryone) => {
    try {
      await messagesAPI.deleteMessage(msgId, forEveryone);
      if (forEveryone) {
        setMessages(prev => prev.map(m => m._id === msgId ? { ...m, deleted: true, content: '' } : m));
      } else {
        setMessages(prev => prev.filter(m => m._id !== msgId));
      }
      toast.info('Message deleted');
    } catch { toast.error('Delete failed'); }
  };

  const handleEdit = async (msgId, content) => {
    try {
      const { data } = await messagesAPI.editMessage(msgId, content);
      setMessages(prev => prev.map(m => m._id === msgId ? { ...m, content: data.content, edited: true } : m));
      toast.success('Message edited');
    } catch { toast.error('Edit failed'); }
  };

  const handleStar = async (msgId) => {
    try {
      const { data } = await messagesAPI.starMessage(msgId);
      setMessages(prev => prev.map(m => {
        if (m._id !== msgId) return m;
        const starred = m.starred || [];
        return { ...m, starred: data.starred ? [...starred, myId] : starred.filter(s=>s!==myId) };
      }));
      toast.success(data.starred ? '⭐ Starred' : 'Unstarred');
    } catch { toast.error('Failed'); }
  };

  const handlePin = (msg) => {
    setPinnedMsg(msg);
    const socket = getSocket();
    const room = [myId, otherId].sort().join('_');
    if (socket) socket.emit('message:pin', { messageId: msg._id, room });
    toast.success('📌 Message pinned');
  };

  const handleForward = (msg) => toast.info(`Forward: "${msg.content?.slice(0,40)}" — select a chat`);

  const jumpToMsg = (msgId) => {
    const el = document.getElementById(`msg-${msgId}`);
    if (el) { el.scrollIntoView({ behavior:'smooth', block:'center' }); el.classList.add('highlight'); setTimeout(() => el.classList.remove('highlight'), 1500); }
  };

  // Voice recording
  const startRec = () => {
    setRecording(true); setRecTime(0);
    recTimerRef.current = setInterval(() => setRecTime(t => t+1), 1000);
  };
  const stopRec = () => {
    clearInterval(recTimerRef.current);
    setRecording(false);
    const fmtDur = `0:${recTime.toString().padStart(2,'0')}`;
    const tempMsg = { _id:`tmp_voice_${Date.now()}`, sender:myId, recipient:otherId, content:'', type:'voice', duration:fmtDur, createdAt:new Date().toISOString(), readBy:[myId] };
    setMessages(prev => [...prev, tempMsg]);
    toast.success(`🎙 Voice note (${fmtDur}) sent`);
  };

  const fmtDur = s => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;

  // Filtered messages for search
  const displayedMsgs = searchQ
    ? messages.filter(m => m.content?.toLowerCase().includes(searchQ.toLowerCase()))
    : messages;

  return (
    <div className="dm-chat">
      {/* ── Active call banner ─────────────────────────────── */}
      {callState && (
        <div className="call-banner">
          <div className="call-banner-left">
            <span className={`call-dot ${callState}`}/>
            <span>{callState === 'calling' ? `Calling ${otherUser.displayName}...` : `${callType==='video'?'📹':'📞'} ${fmtDur(callDur)}`}</span>
          </div>
          <div className="call-banner-right">
            {callState === 'active' && (<>
              <button className={`call-ctl ${muted?'on':''}`} onClick={()=>setMuted(v=>!v)} title={muted?'Unmute':'Mute'}>
                {muted ? <MicOff size={14}/> : <Mic size={14}/>}
              </button>
              {callType==='video' && (
                <button className={`call-ctl ${camOff?'on':''}`} onClick={()=>setCamOff(v=>!v)} title="Camera">
                  {camOff ? <VideoOff size={14}/> : <Video size={14}/>}
                </button>
              )}
            </>)}
            <button className="call-ctl end" onClick={endCall} title="End call"><PhoneOff size={14}/></button>
          </div>
        </div>
      )}

      {/* ── Pinned message ──────────────────────────────────── */}
      {pinnedMsg && (
        <div className="pinned-bar" onClick={() => jumpToMsg(pinnedMsg._id)}>
          <Pin size={12} style={{color:'var(--accent-yellow)'}}/>
          <span>📌 Pinned: {pinnedMsg.content?.slice(0,60)}</span>
          <button onClick={e=>{e.stopPropagation();setPinnedMsg(null)}}><X size={12}/></button>
        </div>
      )}

      {/* ── Header ──────────────────────────────────────────── */}
      <div className="chat-header glass-header">
        <div className="chat-header-left">
          <div style={{position:'relative'}}>
            <img src={getAvatarUrl(otherUser)} alt="" className="avatar" style={{width:38,height:38}}/>
            <span className={`status-dot ${online?'online':'offline'}`} style={{position:'absolute',bottom:0,right:0}}/>
          </div>
          <div>
            <h3 style={{fontSize:'0.92rem',fontWeight:600}}>{otherUser.displayName}</h3>
            <p style={{fontSize:'0.72rem',color:online?'var(--online)':'var(--text-muted)'}}>
              {isTyping
                ? <span style={{display:'flex',alignItems:'center',gap:4}}>
                    <span className="typing-dots"><span/><span/><span/></span> typing
                  </span>
                : online ? 'Active now' : `last seen ${formatMessageDate(otherUser.lastSeen||new Date())}`}
            </p>
          </div>
        </div>
        <div className="chat-header-right">
          <button className="btn-icon" title="Voice call" onClick={()=>startCall('voice')}>
            <Phone size={18}/>
          </button>
          <button className="btn-icon" title="Video call" onClick={()=>startCall('video')}>
            <Video size={18}/>
          </button>
          <button className="btn-icon" title="Search" onClick={()=>setShowSearch(v=>!v)}
            style={{color:showSearch?'var(--primary-light)':undefined}}>
            <Search size={18}/>
          </button>
          <div style={{position:'relative'}}>
            <button className="btn-icon" onClick={()=>setShowMore(v=>!v)}><MoreVertical size={18}/></button>
            {showMore && (<>
              <div className="ctx-menu right" style={{top:36}}>
                <button onClick={()=>{toast.info('View media, links & docs');setShowMore(false)}}><ImageIcon size={13}/> Media</button>
                <button onClick={()=>{setShowSearch(true);setShowMore(false)}}><Search size={13}/> Search</button>
                <button onClick={async()=>{const{data}=await messagesAPI.getStarred();toast.info(`${data.length} starred messages`);setShowMore(false)}}><Star size={13}/> Starred</button>
                <button onClick={()=>{toast.info('Muted');setShowMore(false)}}><Volume2 size={13}/> Mute</button>
                <div className="ctx-sep"/>
                <button className="ctx-danger" onClick={()=>{toast.info('User blocked');setShowMore(false)}}>🚫 Block</button>
              </div>
              <div className="overlay-close" onClick={()=>setShowMore(false)}/>
            </>)}
          </div>
        </div>
      </div>

      {/* ── Search bar ──────────────────────────────────────── */}
      {showSearch && (
        <div className="search-bar">
          <Search size={14}/>
          <input autoFocus placeholder="Search messages..." value={searchQ} onChange={e=>setSearchQ(e.target.value)}/>
          <span style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>{searchQ && `${displayedMsgs.length} found`}</span>
          <button className="btn-icon" onClick={()=>{setShowSearch(false);setSearchQ('')}}><X size={15}/></button>
        </div>
      )}

      {/* ── Encryption notice ────────────────────────────────── */}
      <div className="e2e-notice">
        <Lock size={11}/> Messages are end-to-end encrypted
      </div>

      {/* ── Messages ────────────────────────────────────────── */}
      <div className="messages-area">
        {loading ? <div className="empty-state"><div className="loader"/></div> :
         messages.length === 0 ? (
          <div className="empty-state">
            <img src={getAvatarUrl(otherUser)} alt="" className="avatar" style={{width:72,height:72,marginBottom:8}}/>
            <h3>Chat with {otherUser.displayName}</h3>
            <p>Send a message to start the conversation</p>
          </div>
        ) : (
          displayedMsgs.map((msg,i) => {
            const sid = typeof msg.sender==='object'?(msg.sender._id||msg.sender.id):msg.sender;
            const isMe = sid === myId;
            return (
              <Message key={msg._id||i} msg={msg} isMe={isMe}
                otherUser={otherUser} currentUser={currentUser}
                onReact={handleReact} onDelete={handleDelete}
                onReply={setReplyTo} onEdit={handleEdit}
                onStar={handleStar} onForward={handleForward}
                onPin={handlePin} jumpToMsg={jumpToMsg}
              />
            );
          })
        )}

        {/* FIXED: typing shows only once */}
        {isTyping && (
          <div className="msg-row theirs">
            <img src={getAvatarUrl(otherUser)} alt="" className="avatar" style={{width:30,height:30}}/>
            <div className="msg-wrap">
              <div className="msg-bubble bubble-them typing-bubble">
                <div className="typing-dots"><span/><span/><span/></div>
              </div>
            </div>
          </div>
        )}
        <div ref={endRef}/>
      </div>

      {/* ── Reply banner ─────────────────────────────────────── */}
      {replyTo && (
        <div className="reply-banner">
          <Reply size={13} style={{color:'var(--primary-light)',flexShrink:0}}/>
          <div className="reply-banner-info">
            <span>Replying to {typeof replyTo.sender==='object'?(replyTo.sender._id||replyTo.sender.id)===myId?'yourself':otherUser.displayName:replyTo.sender===myId?'yourself':otherUser.displayName}</span>
            <p>{replyTo.content?.slice(0,80) || `📎 ${replyTo.type}`}</p>
          </div>
          <button className="btn-icon" onClick={()=>setReplyTo(null)}><X size={14}/></button>
        </div>
      )}

      {/* ── Voice recorder ───────────────────────────────────── */}
      {isRecording ? (
        <div className="voice-rec-bar">
          <button className="rec-cancel" onClick={()=>{clearInterval(recTimerRef.current);setRecording(false);setRecTime(0);}}>
            <X size={16}/> Cancel
          </button>
          <div className="rec-indicator">
            <span className="rec-dot"/>
            <span className="rec-time">0:{recTime.toString().padStart(2,'0')}</span>
            <span style={{fontSize:'0.78rem',color:'var(--text-muted)'}}>Recording...</span>
          </div>
          <button className="rec-send" onClick={stopRec}><Send size={16}/></button>
        </div>
      ) : (
        <div className="input-bar">
          {/* Attach */}
          <div style={{position:'relative'}}>
            <button className="btn-icon input-btn" title="Attach" onClick={()=>{setShowAttach(v=>!v);setShowEmoji(false);}}>
              <Paperclip size={18}/>
            </button>
            {showAttach && (<>
              <div className="attach-grid">
                {ATTACH_TYPES.map(a => (
                  <button key={a.label} className="attach-btn"
                    onClick={() => { toast.info(`${a.label}: ${a.info}`); setShowAttach(false); }}>
                    <span className="attach-emoji">{a.icon}</span>
                    <span>{a.label}</span>
                  </button>
                ))}
              </div>
              <div className="overlay-close" onClick={()=>setShowAttach(false)}/>
            </>)}
          </div>

          {/* Emoji */}
          <div style={{position:'relative'}}>
            <button className="btn-icon input-btn" title="Emoji"
              onClick={()=>{setShowEmoji(v=>!v);setShowAttach(false);}}
              style={{color:showEmoji?'var(--primary-light)':undefined}}>
              <Smile size={18}/>
            </button>
            {showEmoji && (<>
              <div className="emoji-full-grid">
                {['😀','😂','😍','🥰','😎','🤔','😅','😭','😡','🤯','👍','❤️','🔥','🎉','✅','⭐','💯','🙏','👏','🎊','🚀','💡','📌','🗓️','📎','🔗','💬','📱','💻','🌟','😊','🤣','😇','🥳','😴','🤓','😏','😒','😳','🤩','💪','🖤','💙','💚','💛','❤️‍🔥','🫶','🤝','✌️','🫂','😘'].map(e=>(
                  <button key={e} className="emoji-btn" onClick={()=>{setInput(p=>p+e);inputRef.current?.focus();}}>{e}</button>
                ))}
              </div>
              <div className="overlay-close" onClick={()=>setShowEmoji(false)}/>
            </>)}
          </div>

          {/* Text input */}
          <div className="input-wrap">
            <textarea ref={inputRef} className="chat-input"
              placeholder={`Message ${otherUser.displayName}...`}
              value={input}
              onChange={e=>{setInput(e.target.value); if(e.target.value.trim()) handleTyping();}}
              onKeyDown={handleKeyDown}
              rows={1}/>
          </div>

          {/* Send or mic */}
          {input.trim() ? (
            <button className="send-btn ready" onClick={handleSend} title="Send"><Send size={18}/></button>
          ) : (
            <button className="send-btn mic-btn" onMouseDown={startRec} onMouseUp={stopRec}
              onTouchStart={startRec} onTouchEnd={stopRec} title="Hold for voice note">
              <Mic size={18}/>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

